import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ExternalLink } from "lucide-react";

const navItems = [
  { label: "Home", href: "/#home", type: "hash" },
  { label: "About", href: "/#about", type: "hash" },
  { label: "Services", href: "/#services", type: "hash" },
  { label: "Pricing", href: "/pricing", type: "route" },
  { label: "Process", href: "/#process", type: "hash" },
  { label: "Contact", href: "/#contact", type: "hash" },
  { label: "AI Editor", href: "https://app.mahaview.in/", type: "external" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const renderLink = (it, mobile = false) => {
    const testId = `${mobile ? "mobile-" : ""}nav-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`;
    const cls = mobile
      ? "text-base text-zinc-200 hover:text-[#FF5A00] inline-flex items-center gap-2"
      : "text-sm text-zinc-300 hover:text-white transition-colors relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#FF5A00] after:transition-all hover:after:w-full inline-flex items-center gap-1.5";
    const onClick = () => mobile && setOpen(false);

    if (it.type === "external") {
      return (
        <a
          key={it.label}
          href={it.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={testId}
          onClick={onClick}
          className={cls}
        >
          {it.label}
          <ExternalLink size={12} className="opacity-70" />
        </a>
      );
    }
    if (it.type === "route") {
      return (
        <Link
          key={it.label}
          to={it.href}
          data-testid={testId}
          onClick={onClick}
          className={cls}
        >
          {it.label}
        </Link>
      );
    }
    // hash
    // If we are not on home page, navigate to home then hash via plain link
    if (pathname !== "/") {
      return (
        <a key={it.label} href={it.href} data-testid={testId} onClick={onClick} className={cls}>
          {it.label}
        </a>
      );
    }
    return (
      <a
        key={it.label}
        href={it.href.replace("/", "")}
        data-testid={testId}
        onClick={onClick}
        className={cls}
      >
        {it.label}
      </a>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#050505]/70 border-b border-white/10"
          : "backdrop-blur-md bg-[#050505]/40 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img
            src="https://customer-assets.emergentagent.com/job_digital-presence-hub-12/artifacts/99t6h1ly_mahaview-new-logo.png"
            alt="Mahaview Enterprises"
            className="h-9 md:h-10 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((it) => renderLink(it))}
        </nav>

        <a
          href="#contact"
          data-testid="nav-cta-button"
          className="hidden lg:inline-flex mv-btn-primary items-center px-5 py-2.5 text-sm font-medium rounded-sm"
        >
          Get Started
        </a>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navItems.map((it) => renderLink(it, true))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta"
              className="mv-btn-primary text-center px-5 py-3 text-sm font-medium rounded-sm"
            >
              Get Started
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
